import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { computeTenantHealth, getStoredTenantHealth, upsertTenantHealth } from "@/services/tenant-health";

export function useTenantHealth(params: { tenantId: string; from: Date; to: Date; refreshToleranceMinutes?: number; autoRefresh?: boolean }) {
  const tolMs = (params.refreshToleranceMinutes ?? 10) * 60 * 1000;
  const key = useMemo(() => ["tenant-health", params.tenantId, params.from.toISOString(), params.to.toISOString()], [params.tenantId, params.from, params.to]);
  const q = useQuery({
    queryKey: key,
    queryFn: async () => {
      const stored = await getStoredTenantHealth(params.tenantId);
      const freshEnough = stored?.last_updated ? (new Date().getTime() - new Date(stored.last_updated).getTime()) < tolMs : false;
      if (stored && !params.autoRefresh && freshEnough) return stored.metrics;
      const metrics = await computeTenantHealth(params.tenantId, { from: params.from, to: params.to });
      await upsertTenantHealth(params.tenantId, metrics);
      return metrics;
    },
  });
  return q;
}

export function useTenantsHealthSummary(params: { tenantIds: string[]; from: Date; to: Date }) {
  const key = useMemo(() => ["tenant-health-summary", params.tenantIds.join(","), params.from.toISOString(), params.to.toISOString()], [params.tenantIds, params.from, params.to]);
  return useQuery({
    queryKey: key,
    queryFn: async () => {
      const items = await Promise.all(params.tenantIds.map((id) => computeTenantHealth(id, { from: params.from, to: params.to })));
      return items;
    },
  });
}

